import React from "react";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";

import vendorImpactImage from "../../../assets/Images/VendorImpact.png";

const fallbackImpactStats = [
  { value: "250+", label: "Vendors Featured & Supported", isGreen: true },
  { value: "24/7", label: "Personalized Vendor Assistance", isGreen: false },
  { value: "45+", label: "NGO Partners Across India", isGreen: false },
  { value: "100%", label: "Transparent Product Distribution", isGreen: true },
];

const VendorImpactSection = () => {
  const home = useSelector((state) => state.cms.home);
  const sections = Array.isArray(home?.data) ? home.data : [];
  const impactCms = sections.find((item) => {
    if (!item) return false;
    const isActive = item.status === 1 || item.status === true;
    if (!isActive) return false;
    return String(item.title || "").toLowerCase() === "impact at scale";
  });

  const sectionTitle = impactCms?.title || "Impact At Scale";
  const sectionDescription =
    impactCms?.description ||
    "We bridge the gap between local vendors and social initiatives by providing round-the-clock personalized support across our extensive network in India. With over 45 NGO partners, we ensure a fully transparent distribution process that empowers more than 250 featured vendors.";
  const sectionImage = impactCms?.image || vendorImpactImage;

  const cmsStats = Array.isArray(impactCms?.relatedCMS)
    ? impactCms.relatedCMS
        .filter((item) => item && (item.status === 1 || item.status === true))
        .map((item, index) => ({
          value: item?.description || "",
          label: item?.title || "",
          isGreen: index === 0 || index === 3,
        }))
    : [];
  const impactStats = cmsStats.length > 0 ? cmsStats : fallbackImpactStats;

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "94.44%" },
        maxWidth: "1440px",
        mx: "auto",
        px: { xs: "16px", md: 0 },
        mb: { xs: "30px", md: "60px" },
        borderRadius: { xs: "0", md: "28px", lg: "34px" },
        background: { xs: "transparent", md: "#1D1D1B" },
        overflow: { xs: "visible", md: "hidden" },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { md: "stretch" },
        minHeight: { xs: "auto", md: "min(560px, 85vh)", lg: "610px" },
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          flex: { md: "1 1 50%" },
          minWidth: 0,
          p: { xs: "30px 20px 24px", sm: "40px 24px", md: "40px 32px", lg: "60px" },
          order: { xs: 2, md: 1 },
          background: { xs: "#1D1D1B", md: "transparent" },
          borderRadius: { xs: "0 0 16.907px 17px", md: 0 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 0,
            width: "100%",
            minWidth: 0,
            "@media (min-width: 800px) and (max-width: 899.95px)": {
              flexDirection: "row",
              alignItems: "flex-start",
              gap: "20px",
            },
          }}
        >
          <Box
            sx={{
              flex: "none",
              minWidth: 0,
              width: "100%",
              "@media (min-width: 800px) and (max-width: 899.95px)": {
                flex: "1 1 48%",
                width: "auto",
              },
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: { xs: "34px", md: "34px", lg: "36px" }, lineHeight: { xs: "100%", md: "100%" }, color: "#FFFFFF", mb: { xs: "8px", md: "16px" } }}>
              {sectionTitle}
            </Typography>

            <Typography
              sx={{
                fontWeight: 400,
                fontSize: { xs: "12px", md: "15px", lg: "16px" },
                lineHeight: { xs: "19.416px", md: "26px", lg: "28px" },
                color: "rgba(255,255,255,0.8)",
                maxWidth: { md: "100%", lg: "600px" },
                mb: { xs: "28px", sm: "28px", md: "32px", lg: "40px" },
                "@media (min-width: 800px) and (max-width: 899.95px)": {
                  mb: 0,
                },
              }}
            >
              {sectionDescription}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: "none",
              minWidth: 0,
              width: "100%",
              alignSelf: "stretch",
              "@media (min-width: 800px) and (max-width: 899.95px)": {
                flex: "1 1 52%",
                width: "auto",
              },
            }}
          >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            position: "relative",
            maxWidth: { xs: "519px", sm: "519px", md: "100%", lg: "519px" },
            width: "100%",
            "@media (min-width: 800px) and (max-width: 899.95px)": {
              maxWidth: "100%",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              top: "50%",
              left: 0,
              right: 0,
              height: "1px",
              background: "rgba(255,255,255,0.1)",
              zIndex: 1,
            },
            "&::after": {
              content: '""',
              position: "absolute",
              left: "50%",
              top: 0,
              bottom: 0,
              width: "1px",
              background: "rgba(255,255,255,0.1)",
              zIndex: 1,
            },
          }}
        >
          {impactStats.map((item, index) => (
            <Box
              key={item.label}
              sx={{
                minHeight: { xs: "106px", sm: "106px", md: "96px", lg: "106px" },
                py: {
                  xs: "16px",
                  sm: "16px",
                  md: index < 2 ? "0 0 20px" : "20px 0 0",
                  lg: index < 2 ? "0 0 26px" : "26px 0 0",
                },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                pl: {
                  xs: index % 2 === 0 ? "0px" : "20px",
                  sm: index % 2 === 0 ? "0px" : "20px",
                  md: index % 2 === 0 ? "8px" : "28px",
                  lg: index % 2 === 0 ? "12px" : "40px",
                },
                pr: {
                  xs: "0px",
                  sm: "0px",
                  md: index % 2 === 0 ? "8px" : "16px",
                  lg: index % 2 === 0 ? "12px" : "38px",
                },
                minWidth: 0,
                "@media (min-width: 800px) and (max-width: 899.95px)": {
                  pl: index % 2 === 0 ? "4px" : "14px",
                  pr: index % 2 === 0 ? "4px" : "10px",
                  py: index < 2 ? "0 0 12px" : "12px 0 0",
                  minHeight: "unset",
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "22px", sm: "22px", md: "clamp(26px, 3.2vw, 38px)", lg: "45.333px" },
                  lineHeight: { xs: "28.643px", sm: "28.643px", md: 1.2, lg: "55.628px" },
                  color: item.isGreen ? "#A9C74E" : "#FFFFFF",
                  wordBreak: "break-word",
                  "@media (min-width: 800px) and (max-width: 899.95px)": {
                    fontSize: "clamp(18px, 2.8vw, 26px)",
                    lineHeight: 1.2,
                  },
                }}
              >
                {item.value}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "12px", sm: "12px", md: "13px", lg: "14px" },
                  lineHeight: { xs: "19.416px", sm: "19.416px", md: "21px", lg: "22.9px" },
                  color: "#FFFFFF",
                  letterSpacing: "-0.14px",
                  whiteSpace: { xs: "normal", md: "normal", lg: "nowrap" },
                  maxWidth: { xs: "122px", sm: "122px", md: "100%", lg: "none" },
                  overflowWrap: "anywhere",
                  "@media (min-width: 800px) and (max-width: 899.95px)": {
                    fontSize: "11px",
                    lineHeight: "19px",
                    maxWidth: "100%",
                  },
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          flex: { md: "1 1 50%" },
          minWidth: 0,
          minHeight: { xs: "188px", sm: "480px", md: "min(560px, 85vh)", lg: "610px" },
          "@media (min-width: 600px) and (max-width: 799.95px)": {
            minHeight: "min(52vw, 320px)",
          },
          order: { xs: 1, md: 2 },
          position: "relative",
          alignSelf: { md: "stretch" },
        }}
      >
        <Box
          component="img"
          src={sectionImage}
          alt="Vendor impact"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            inset: 0,
            borderRadius: {
              xs: "20px 20px 0 0",
              md: "0 28px 28px 0",
              lg: "0 34px 34px 0",
            },
            objectPosition: { xs: "center 30%", md: "center" },
          }}
        />
      </Box>
    </Box>
  );
};

export default VendorImpactSection;
